import { useEffect, useState } from 'react'

export default function useKeyboard(options = {}) {
  const { enabled = true, threshold = 80 } = options

  const [state, setState] = useState({
    isKeyboardOpen: false,
    keyboardHeight: 0,
  })

  useEffect(() => {
    if (!enabled) {
      setState({ isKeyboardOpen: false, keyboardHeight: 0 })
      return
    }

    if (typeof window === 'undefined') return

    const clampToZero = (v) => (v > 0 ? v : 0)

    let baselineInnerHeight = window.innerHeight

    const updateFromVisualViewport = () => {
      const vv = window.visualViewport
      if (!vv) return false

      const visibleViewportHeight = vv.height + vv.offsetTop
      const keyboardHeight = clampToZero(baselineInnerHeight - visibleViewportHeight)
      const isKeyboardOpen = keyboardHeight > threshold

      setState({ isKeyboardOpen, keyboardHeight: isKeyboardOpen ? keyboardHeight : 0 })
      return true
    }

    const updateFromInnerHeight = () => {
      const keyboardHeight = clampToZero(baselineInnerHeight - window.innerHeight)
      const isKeyboardOpen = keyboardHeight > threshold

      setState({ isKeyboardOpen, keyboardHeight: isKeyboardOpen ? keyboardHeight : 0 })
    }

    const onFocusIn = (e) => {
      const tag = e?.target?.tagName
      const isEditable = e?.target?.isContentEditable
      const isTextField = tag === 'INPUT' || tag === 'TEXTAREA' || isEditable

      if (isTextField) {
        baselineInnerHeight = window.innerHeight
      }
    }

    const onFocusOut = (e) => {
      const tag = e?.target?.tagName
      const isEditable = e?.target?.isContentEditable
      const isTextField = tag === 'INPUT' || tag === 'TEXTAREA' || isEditable

      if (isTextField) {
        baselineInnerHeight = window.innerHeight
        setState({ isKeyboardOpen: false, keyboardHeight: 0 })
      }
    }

    const onResize = () => {
      if (!updateFromVisualViewport()) {
        updateFromInnerHeight()
      }
    }

    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    window.addEventListener('resize', onResize)

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onResize)
      window.visualViewport.addEventListener('scroll', onResize)
    }

    onResize()

    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
      window.removeEventListener('resize', onResize)

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onResize)
        window.visualViewport.removeEventListener('scroll', onResize)
      }
    }
  }, [enabled, threshold])

  return state
}
